import { z } from "zod";
import { FieldValues, Path } from "react-hook-form";
import { StepConfig } from "./use-multi-step-form";

export interface StepDefinition<
  TFormData extends FieldValues,
  TFields extends readonly Path<TFormData>[] = readonly Path<TFormData>[],
> {
  id: string;
  fields: TFields;
  schema: z.ZodSchema<Pick<TFormData, TFields[number]>>;
  canContinue?: (data: Pick<TFormData, TFields[number]>) => boolean | Promise<boolean>;
  onStepComplete?: (data: Pick<TFormData, TFields[number]>) => void | Promise<void>;
}

export function defineStep<TFormData extends FieldValues, TFields extends Path<TFormData>[]>(
  step: StepDefinition<TFormData, TFields>,
): StepConfig<TFormData, TFields> {
  return step;
}

export function createStepSchema<TFormData extends FieldValues, TFields extends readonly Path<TFormData>[]>(
  fullSchema: z.ZodSchema<TFormData>,
  fields: TFields,
): z.ZodSchema<Pick<TFormData, TFields[number]>> {
  // Unwrap ZodEffects to get the underlying ZodObject
  // When using .refine(), .transform(), or .superRefine() on a ZodObject,
  // Zod wraps it in a ZodEffects schema. We need to unwrap this to access
  // the original object shape for creating step-specific schemas.

  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/restrict-plus-operands --
   * Accessing Zod's internal structure (_def, shape, typeName) which isn't publicly typed.
   * This is necessary to unwrap ZodEffects and extract the underlying ZodObject shape.
   */
  let baseSchema: any = fullSchema;

  // Keep unwrapping ZodEffects until we get to the base schema
  while (baseSchema._def && baseSchema._def.typeName === "ZodEffects") {
    baseSchema = baseSchema._def.schema;
  }

  // Check if it's a ZodObject and access shape safely
  if (baseSchema._def && baseSchema._def.typeName === "ZodObject") {
    const fullSchemaShape = baseSchema.shape as Record<string, z.ZodSchema>;
    const stepSchemaShape: Record<string, z.ZodSchema> = {};

    fields.forEach((field) => {
      const fieldKey = field as string;
      if (fieldKey in fullSchemaShape) {
        stepSchemaShape[fieldKey] = fullSchemaShape[fieldKey];
      }
    });

    return z.object(stepSchemaShape) as unknown as z.ZodSchema<Pick<TFormData, TFields[number]>>;
  }

  throw new Error(
    "createStepSchema: fullSchema must be a ZodObject or ZodEffects<ZodObject>. Received schema of type " +
      (baseSchema._def?.typeName ?? typeof fullSchema) +
      ".",
  );
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/restrict-plus-operands */
}

export class MultiStepFormBuilder<
  TFormData extends FieldValues,
  TStepConfig extends StepConfig<TFormData, readonly Path<TFormData>[]> = StepConfig<
    TFormData,
    readonly Path<TFormData>[]
  >,
> {
  private steps: TStepConfig[] = [];
  private fullSchema: z.ZodSchema<TFormData>;

  constructor(fullSchema: z.ZodSchema<TFormData>) {
    this.fullSchema = fullSchema;
  }

  addStep<TFields extends readonly Path<TFormData>[]>(
    config: Omit<TStepConfig, "schema"> & {
      fields: TFields;
      schema?: z.ZodSchema<Pick<TFormData, TFields[number]>>;
    },
  ): this {
    const stepSchema = config.schema ?? createStepSchema(this.fullSchema, config.fields as readonly Path<TFormData>[]);

    this.steps.push({
      ...config,
      schema: stepSchema,
    } as unknown as TStepConfig);

    return this;
  }

  build(): TStepConfig[] {
    return this.steps;
  }
}

export function createMultiStepForm<
  TFormData extends FieldValues,
  TStepConfig extends StepConfig<TFormData> = StepConfig<TFormData>,
>(fullSchema: z.ZodSchema<TFormData>): MultiStepFormBuilder<TFormData, TStepConfig> {
  return new MultiStepFormBuilder<TFormData, TStepConfig>(fullSchema);
}
